import React, { useState, useEffect } from "react";
import { ChevronLeft, Music, Send, CheckCircle, Wifi, WifiOff } from "lucide-react";
import { auth, db } from "../firebase-config";
import { doc, setDoc, onSnapshot, arrayUnion, updateDoc } from "firebase/firestore";

export const NameThatLyric = ({ onBack, clientObj, userObj }) => {
  const [currentSong, setCurrentSong] = useState(null);
  const [userGuess, setUserGuess] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [waitingForHost, setWaitingForHost] = useState(true);

  // Set up Firebase listener for the current game
  useEffect(() => {
    if (!clientObj?.clientId) {
      console.log('No client ID available');
      setWaitingForHost(true);
      return;
    }

    console.log('Setting up Name That Lyric listener for client:', clientObj.clientId);
    const gameRef = doc(db, "users", clientObj.clientId, "namethatlyric", "current");

    const unsubscribe = onSnapshot(gameRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        console.log('Name That Lyric data received:', data);

        // Check if there's an active song/question
        if (data.currentQuestion) {
          setIsConnected(true);
          setWaitingForHost(false);

          // Check if user already submitted for this question
          const userId = userObj?.uid || userObj?.id;
          const userAlreadyAnswered = data.submissions?.some(
            submission => submission.userId === userId && submission.questionId === data.currentQuestion.id
          );

          if (userAlreadyAnswered) {
            // User already submitted, show their previous answer
            const userSubmission = data.submissions.find(
              submission => submission.userId === userId && submission.questionId === data.currentQuestion.id
            );
            setUserGuess(userSubmission?.userGuess || "");
            setSubmitted(true);
          } else {
            // New question, reset submission state
            setSubmitted(false);
            setUserGuess("");
          }

          // Set the current song data
          setCurrentSong({
            id: data.currentQuestion.id || `question-${Date.now()}`,
            artist: data.currentQuestion.artist || "Unknown Artist",
            title: data.currentQuestion.title || "Unknown Song",
            albumArt: data.currentQuestion.albumArt || data.currentQuestion.coverart || "",
            lyricBefore: data.currentQuestion.lyricBefore || "",
            missingLyric: data.currentQuestion.missingLyric || [],
            lyricAfter: data.currentQuestion.lyricAfter || "",
            fullLine: data.currentQuestion.fullLine || ""
          });
        } else {
          console.log('No current question set by host');
          setWaitingForHost(true);
          setIsConnected(false);
          setCurrentSong(null);
        }
      } else {
        console.log('Name That Lyric document does not exist');
        setWaitingForHost(true);
        setIsConnected(false);
        setCurrentSong(null);
      }
    }, (error) => {
      console.error("Error listening to Name That Lyric game:", error);
      setIsConnected(false);
      setWaitingForHost(true);
    });

    return () => unsubscribe();
  }, [clientObj, userObj]);

  const handleSubmit = async () => {
    if (!userGuess.trim() || !currentSong || !clientObj?.clientId || !userObj) return;

    setSubmitted(true);

    // Prepare submission data
    const submissionData = {
      userId: auth.currentUser.uid,
      displayName: userObj.displayName || userObj.name || userObj.email || "Anonymous",
      photoURL: userObj.photoURL || userObj.avatar || userObj.profilePicture || "",
      questionId: currentSong.id,
      songTitle: currentSong.title,
      artist: currentSong.artist,
      userGuess: userGuess.trim(),
      timestamp: new Date().toISOString()
    };

    // Save submission to Firebase for admin to judge
    try {
      const gameRef = doc(db, "users", clientObj.clientId, "namethatlyric", "current");

      await updateDoc(gameRef, {
        submissions: arrayUnion(submissionData)
      });

      console.log("Answer submitted for admin review");
    } catch (error) {
      console.error("Error submitting answer:", error);
      // If document doesn't exist, create it
      try {
        const gameRef = doc(db, "users", clientObj.clientId, "namethatlyric", "current");
        await setDoc(gameRef, {
          submissions: [submissionData]
        }, { merge: true });
        console.log("Answer submitted (document created)");
      } catch (createError) {
        console.error("Error creating submission:", createError);
      }
    }
  };

  // Show waiting screen if no question is available
  if (waitingForHost || !currentSong) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
        <div className="relative max-w-md mx-auto min-h-screen">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 opacity-20 blur-2xl"></div>

          <div className="relative bg-purple-950/90 backdrop-blur-sm min-h-screen border-x border-cyan-400/20">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-900/50 to-indigo-900/50 backdrop-blur">
              <button
                onClick={onBack}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Back"
              >
                <ChevronLeft size={24} className="text-white" />
              </button>

              <h1 className="text-2xl font-bold text-white tracking-wider">
                NAME THAT LYRIC
              </h1>

              <div className="flex items-center gap-2">
                <WifiOff size={20} className="text-red-400" />
              </div>
            </div>

            {/* Waiting Message */}
            <div className="flex flex-col items-center justify-center min-h-[80vh] px-6">
              <div className="text-center">
                <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center animate-pulse">
                  <Music size={64} className="text-white" />
                </div>

                <h2 className="text-2xl font-bold text-white mb-4">
                  Waiting for Host
                </h2>
                <p className="text-white/70 mb-8">
                  The host will start a new question soon...
                </p>

                <button
                  onClick={onBack}
                  className="px-6 py-3 bg-red-500/20 border border-red-500/50 rounded-full text-red-400 hover:bg-red-500/30 transition-colors"
                >
                  Leave Game
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      <div className="relative max-w-md mx-auto min-h-screen">
        {/* Neon border effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 opacity-20 blur-2xl"></div>

        <div className="relative bg-purple-950/90 backdrop-blur-sm min-h-screen border-x border-cyan-400/20">
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-900/50 to-indigo-900/50 backdrop-blur">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Back"
            >
              <ChevronLeft size={24} className="text-white" />
            </button>

            <div className="text-center">
              <h1 className="text-2xl font-bold text-white tracking-wider">
                NAME THAT LYRIC
              </h1>
              <div className="flex items-center justify-center gap-2 text-sm mt-1">
                {isConnected ? (
                  <>
                    <Wifi size={14} className="text-green-400" />
                    <span className="text-green-400">Connected</span>
                  </>
                ) : (
                  <>
                    <WifiOff size={14} className="text-yellow-400" />
                    <span className="text-yellow-400">Connecting...</span>
                  </>
                )}
              </div>
            </div>

            <div className="w-10" /> {/* Spacer */}
          </div>

          {currentSong && (
            <>
              {/* Song Display */}
              <div className="px-4 py-4">
                <div className="bg-gradient-to-r from-cyan-400/10 to-purple-500/10 rounded-2xl p-4 border border-cyan-400/20">
                  <div className="flex items-center gap-4">
                    {currentSong.albumArt ? (
                      <img
                        src={currentSong.albumArt}
                        alt={`${currentSong.title} cover`}
                        className="w-20 h-20 rounded-lg object-cover shadow-lg"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                        <Music size={32} className="text-white" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-white font-bold text-lg">{currentSong.title}</h3>
                      <p className="text-white/70">{currentSong.artist}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lyrics Section */}
              <div className="px-4 py-6">
                <div className="bg-purple-800/30 rounded-2xl p-6 border border-purple-600/50">
                  <p className="text-white/90 text-xl leading-relaxed mb-4">
                    "{currentSong.lyricBefore}
                  </p>

                  {/* Missing Lyrics Input */}
                  <div className="bg-purple-900/50 rounded-lg p-3 border-2 border-dashed border-cyan-400/50 mb-4">
                    {!submitted ? (
                      <input
                        type="text"
                        value={userGuess}
                        onChange={(e) => setUserGuess(e.target.value)}
                        placeholder="Fill in the missing lyrics..."
                        className="w-full bg-black/70 text-white placeholder-white/40 focus:outline-none focus:bg-black/90 text-xl font-semibold px-2 py-1 rounded transition-colors"
                        aria-label="Missing lyrics input"
                      />
                    ) : (
                      <div className="text-white">
                        <p className="text-sm text-cyan-400 mb-1">Your answer:</p>
                        <p className="text-lg font-bold">{userGuess}</p>
                        <p className="text-sm text-green-400 mt-2">✓ Submitted for review</p>
                      </div>
                    )}
                  </div>

                  <p className="text-white/90 text-xl leading-relaxed">
                    {currentSong.lyricAfter}"
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="px-4 py-4">
                {!submitted ? (
                  <button
                    onClick={handleSubmit}
                    disabled={!userGuess.trim()}
                    className={`w-full py-4 rounded-full font-bold text-white transition-all flex items-center justify-center gap-2
                      ${userGuess.trim()
                        ? 'bg-gradient-to-r from-cyan-400 to-purple-500 shadow-[0_0_20px_rgba(34,211,238,0.5)] hover:scale-105'
                        : 'bg-purple-800/50 cursor-not-allowed opacity-50'
                      }`}
                    aria-label="Submit answer"
                  >
                    <Send size={20} />
                    Submit Answer
                  </button>
                ) : (
                  <button
                    disabled
                    className="w-full py-4 rounded-full font-bold text-white bg-gradient-to-r from-purple-500 to-pink-500 opacity-75 cursor-not-allowed flex items-center justify-center gap-2"
                    aria-label="Waiting for host"
                  >
                    <CheckCircle size={20} />
                    Waiting for Host to Continue...
                  </button>
                )}
              </div>

              {/* Info for Host */}
              <div className="px-4 py-2">
                <p className="text-center text-white/50 text-sm">
                  Your answer will be sent to the host for review
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};